import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import ReactPlayer from 'react-player';
import OverlayLoader from './components/Layout/OverlayLoader';


function useWindowSize() {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
}


function StreamPlayer({ streamUrl, participant, chat, onPlayerReady }) {

    const playerRef = useRef(null);
    const [lastResetToLive, setLastResetToLive] = useState(Date.now());

    const [width, height] = useWindowSize();

    let heightcustom = 100 + 'vh';
    let widthcustom = (100 * 1.777) + 'vh';

    if(height * 1.77 > width) {
        heightcustom = width / 1.777 + 'px';
        widthcustom = width + 'px';
    } else {
        heightcustom = 100 + 'vh';
        widthcustom = (100 * 1.777) + 'vh';
    }

    // Add new state for tracking play status
    const [isPlaying, setIsPlaying] = useState(true);

    // Modify state to handle both error and completion
    const [needsRestart, setNeedsRestart] = useState(false);

    // Constants for retry logic
    const MAX_RETRIES = 5;  // Maximum number of retry attempts
    const RETRY_WINDOW = 5 * 60 * 1000;  // 5 minutes in milliseconds
    const RETRY_DELAY = 1000;  // Delay between retries in milliseconds

    // Retry tracking state
    const [retryCount, setRetryCount] = useState(0);
    const [firstRetryTimestamp, setFirstRetryTimestamp] = useState(null);

    // Add new state for controlling overlay visibility
    const [showOverlay, setShowOverlay] = useState(false);


    // Updated handlePlayPause function
    const handlePlayPause = () => {


        if (isPlaying) {
            setTimeout(() => {
                playerRef.current.seekTo('live');  // Seek to live position when resuming
                playerRef.current.getInternalPlayer().playVideo();  // Play after 1 second delay
            }, 1000);
        }

        setIsPlaying(!isPlaying);
    };

    // Reset retry counters
    const resetRetryCounters = () => {
        setRetryCount(0);
        setFirstRetryTimestamp(null);
        setNeedsRestart(false);
    };

    // Handle playback issues (error or completion)
    const handlePlaybackIssue = () => {
        const now = Date.now();

        // Initialize first retry timestamp if this is the first retry
        if (firstRetryTimestamp === null) {
            setFirstRetryTimestamp(now);
            setRetryCount(1);
        } else {
            // Check if we're still within the retry window
            if (now - firstRetryTimestamp > RETRY_WINDOW) {
                // Reset counters if we're outside the window
                resetRetryCounters();
                setFirstRetryTimestamp(now);
                setRetryCount(1);
            } else {
                // Increment retry count if within window
                setRetryCount(prev => prev + 1);
            }
        }

        setNeedsRestart(true);
        setIsPlaying(false);
    };

    // Handle playback completion
    const handlePlaybackEnded = () => {
        handlePlaybackIssue();
    };

    // Recovery mechanism
    useEffect(() => {
        if (needsRestart) {
            // Check if we've exceeded retry limits
            if (retryCount >= MAX_RETRIES) {
                setNeedsRestart(false);
                window.location.reload(); // Refresh the page when max retries reached
                return;
            }

            const timer = setTimeout(() => {
                if (playerRef.current) {
                    setNeedsRestart(false);
                    setIsPlaying(true);
                    playerRef.current.seekTo('live');
                }
            }, RETRY_DELAY);

            return () => clearTimeout(timer);
        }
    }, [needsRestart, retryCount]);

    // Reset retry counters on successful playback
    const handlePlay = () => {
        setIsPlaying(true);
        resetRetryCounters();
    };

    const handleBufferEnd = () => {
        if (Date.now() - lastResetToLive > 15000) {
            playerRef.current.seekTo('live');
            setLastResetToLive(Date.now());
        }
    };

    const handlePlayerReady = () => {
        setIsPlaying(true);  // Force play when player is ready
        onPlayerReady();
    };

    // Add useEffect to ensure playing state is true on mount
    useEffect(() => {
        setIsPlaying(true);
    }, []); // Empty dependency array means this runs once on mount

    // First useEffect for bandwidth monitoring
    useEffect(() => {
        const checkBandwidth = () => {
            const connection = navigator.connection ||
                navigator.mozConnection ||
                navigator.webkitConnection;

            // if (connection) {
            //     console.log(
            //         '################\n',
            //         {
            //             bandwidth: `${connection.downlink} Mbps`,
            //             networkType: connection.effectiveType,
            //             rtt: `${connection.rtt} ms`,
            //         },
            //         '\n################'
            //     );
            // }
        };

        // Initial check
        checkBandwidth();

        const connection = navigator.connection ||
            navigator.mozConnection ||
            navigator.webkitConnection;

        if (connection) {
            connection.addEventListener('change', checkBandwidth);
            return () => connection.removeEventListener('change', checkBandwidth);
        }
    }, []);

    // New useEffect for monitoring YouTube quality
    useEffect(() => {
        const checkQuality = () => {
            const player = playerRef.current?.getInternalPlayer();
            // if (player?.getPlaybackQuality) {
            //     console.log(
            //         '################\n',
            //         {
            //             youtubeQuality: player.getPlaybackQuality()
            //         },
            //         '\n################'
            //     );
            // }
        };

        // Check quality every second
        const intervalId = setInterval(checkQuality, 10000);

        return () => clearInterval(intervalId);
    }, []);

    // Also keep the quality change event handler
    const handleQualityChange = (quality) => {
        console.log(
            '################\n',
            {
                youtubeQuality: quality
            },
            '\n################'
        );
    };

    // Add useEffect to handle delayed showing of overlay
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowOverlay(true);
        }, 10000);

        return () => clearTimeout(timer);
    }, []); // Run once on mount

    return (
        <div>
            <ReactPlayer
                ref={playerRef}
                url={streamUrl}
                className={'videoPlayer'}
                playing={isPlaying}
                onPause={handlePlayPause}
                controls={true}
                onBufferEnd={handleBufferEnd}
                onReady={handlePlayerReady}
                width={participant || chat ? (width - 340) : widthcustom}
                height={participant || chat ? (width - 342) / 1.777 : heightcustom}
                // width={width}
                // height={height}
                onPlaybackQualityChange={handleQualityChange}
                onError={handlePlaybackIssue}
                onEnded={handlePlaybackEnded}
                key={needsRestart ? Date.now() : 'initial'}
                style={{ opacity: isPlaying ? 1 : 0, transition: 'opacity 0.3s ease' }}
            />

            {/* {showOverlay && ( */}
                {/* <> */}
                    {/* <div className="bottom-overlay" style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: participant || chat ? (width - 320) : '100%',
                        height: '46px',
                }} /> */}

                {/* <div className="bottom-overlay" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                        width: participant || chat ? (width - 320) : '100%',
                        height: '46px',
                    }} /> */}
                {/* </> */}
            {/* )} */}

            <OverlayLoader show={!isPlaying} />

        </div>

    );
}

export default StreamPlayer;
